import { Task } from '@models/task.model';
import { addDaysToDate, parseTaskDate, toCalendarISOString } from '@utils/dateOnlyParser';

/** Days of empty runway shown after the last thing that ends. */
export const PROJECT_VIEW_RUNWAY_DAYS = 10;

const MIN_VALID_DATE = new Date(2000, 0, 1, 12);
const MAX_VALID_DATE = new Date(2100, 11, 31, 12);

/** Both bounds are YYYY-MM-DD; `end` is INCLUSIVE (FullCalendar's exclusive end is the caller's job). */
export interface ProjectRange {
  start: string;
  end: string;
}

interface ComputeProjectRangeArgs {
  projectStartDate?: string | Date | null;
  projectEndDate?: string | Date | null;
  tasks?: Pick<Task, 'startTime' | 'endTime'>[];
  today?: Date;
}

/** Parses to local noon; unparseable values and dates outside 2000-2100 are treated as data errors. */
const parseValid = (value: string | Date | null | undefined): Date | null => {
  const parsed = parseTaskDate(value);
  if (!parsed) return null;
  return parsed < MIN_VALID_DATE || parsed > MAX_VALID_DATE ? null : parsed;
};

/**
 * The visible range of the whole-project ("Project") schedule view.
 *
 * - start = project start, else earliest task start, else today. A task that starts before the
 *   project start pulls `start` earlier so nothing is cut off on the left.
 * - end = max(project end, every task end, every task start) + 10 days. With no dates at all the
 *   latest end is `start`, so the range is start .. start + 10 days.
 *
 * Pure and date-only: everything is parsed to local noon (same convention as
 * `getScheduleAnchorDate`) so `'2026-09-01'` never drifts a day across DST or timezones.
 */
export function computeProjectRange({
  projectStartDate,
  projectEndDate,
  tasks = [],
  today = new Date(),
}: ComputeProjectRangeArgs): ProjectRange {
  const projectStart = parseValid(projectStartDate);
  const projectEnd = parseValid(projectEndDate);

  let earliestTaskStart: Date | null = null;
  let latestTaskDate: Date | null = null;
  for (const task of tasks) {
    const taskStart = parseValid(task.startTime);
    const taskEnd = parseValid(task.endTime);
    if (taskStart && (!earliestTaskStart || taskStart < earliestTaskStart)) earliestTaskStart = taskStart;
    for (const candidate of [taskStart, taskEnd]) {
      if (candidate && (!latestTaskDate || candidate > latestTaskDate)) latestTaskDate = candidate;
    }
  }

  let start: Date;
  if (projectStart && earliestTaskStart) {
    start = earliestTaskStart < projectStart ? earliestTaskStart : projectStart;
  } else {
    start = projectStart ?? earliestTaskStart ?? parseTaskDate(today)!;
  }

  let latestEnd = start;
  for (const candidate of [projectEnd, latestTaskDate]) {
    if (candidate && candidate > latestEnd) latestEnd = candidate;
  }

  return {
    start: toCalendarISOString(start),
    end: toCalendarISOString(addDaysToDate(latestEnd, PROJECT_VIEW_RUNWAY_DAYS)),
  };
}
